import { useState, useEffect, useCallback } from 'react';

export interface TabConfig {
  id: string;
  name: string;
  visibleColumns: string[];
  filters: Record<string, string>;
  sortState: { field: string | null; direction: 'asc' | 'desc' | null };
  hiddenFounderIds?: string[]; // Legacy: Founders hidden/deleted in this tab
  founderIds?: string[]; // Founders that belong to this tab (if undefined, show all except hidden)
  columnWidths?: Record<string, number>; // Column widths for this tab
}

const DEFAULT_TAB_NAME = 'All People';

const getStorageKey = (userId: string) => `people_table_tabs_${userId}`;

const loadTabsFromStorage = (userId: string): TabConfig[] => {
  const STORAGE_KEY = getStorageKey(userId);
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error loading tabs from storage:', error);
  }
  
  // Check if there's an existing visibleColumns preference in localStorage (user-specific)
  let initialVisibleColumns = ['no', 'name', 'update'];
  try {
    const existingColumns = localStorage.getItem(`people_visible_columns_${userId}`);
    if (existingColumns) {
      const parsed = JSON.parse(existingColumns);
      if (Array.isArray(parsed) && parsed.length > 0) {
        initialVisibleColumns = parsed;
      }
    }
  } catch (error) {
    // Use default if parsing fails
  }
  
  // Return default tab if nothing in storage
  // Note: default tab can use legacy behavior (no founderIds = show all)
  return [{
    id: 'default',
    name: DEFAULT_TAB_NAME,
    visibleColumns: initialVisibleColumns,
    filters: {},
    sortState: { field: null, direction: null },
    founderIds: undefined // Undefined means show all founders (legacy behavior for default tab only)
  }];
};

const saveTabsToStorage = (tabs: TabConfig[], userId: string) => {
  const STORAGE_KEY = getStorageKey(userId);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch (error) {
    console.error('Error saving tabs to storage:', error);
  }
};

export const useTableTabs = (userId: string) => {
  const [tabs, setTabs] = useState<TabConfig[]>(() => loadTabsFromStorage(userId));
  const [activeTabId, setActiveTabId] = useState<string>(tabs[0]?.id || 'default');

  // Load tabs on mount and migrate legacy tabs
  useEffect(() => {
    const loadedTabs = loadTabsFromStorage(userId);
    // Migrate tabs that don't have founderIds: initialize with empty array (except default tab)
    // This ensures all existing tabs get founderIds initialized
    const migratedTabs = loadedTabs.map(tab => {
      // Always ensure the default tab is named "All People"
      if (tab.id === 'default') {
        return { ...tab, name: DEFAULT_TAB_NAME };
      }
      // If founderIds is undefined and it's not the default tab, initialize as empty
      if (tab.founderIds === undefined) {
        return { ...tab, founderIds: [] };
      }
      return tab;
    });
    setTabs(migratedTabs);
    setActiveTabId(migratedTabs[0]?.id || 'default');
  }, [userId]);

  // Save tabs whenever they change
  useEffect(() => {
    saveTabsToStorage(tabs, userId);
  }, [tabs, userId]);

  const activeTab = tabs.find(tab => tab.id === activeTabId) || tabs[0];

  const addTab = useCallback(() => {
    const newTabId = `tab-${Date.now()}`;
    const existingNames = tabs.map(t => t.name);
    let newTabName = DEFAULT_TAB_NAME;
    let counter = 2;
    
    // Find next available name
    while (existingNames.includes(newTabName)) {
      newTabName = `Table ${counter}`;
      counter++;
    }
    
    const newTab: TabConfig = {
      id: newTabId,
      name: newTabName,
      visibleColumns: activeTab.visibleColumns,
      filters: {},
      sortState: { field: null, direction: null },
      founderIds: [], // New tabs start with empty founder list
      columnWidths: activeTab.columnWidths ? { ...activeTab.columnWidths } : undefined // Copy column widths from active tab
    };
    
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  }, [tabs, activeTab]);

  const updateActiveTab = useCallback((updates: Partial<Omit<TabConfig, 'id' | 'name'>>) => {
    setTabs(prev => prev.map(tab => 
      tab.id === activeTabId 
        ? { ...tab, ...updates }
        : tab
    ));
  }, [activeTabId]);

  const renameTab = useCallback((tabId: string, newName: string) => {
    // Prevent renaming the default tab
    if (tabId === 'default') {
      return;
    }
    setTabs(prev => prev.map(tab => 
      tab.id === tabId 
        ? { ...tab, name: newName.trim() || DEFAULT_TAB_NAME }
        : tab
    ));
  }, []);

  const deleteTab = useCallback(async (tabId: string) => {
    console.log('deleteTab called with:', tabId, 'Current tabs:', tabs.length);
    if (tabs.length <= 1) {
      // Don't allow deleting the last tab
      console.log('Cannot delete - only one tab remaining');
      return;
    }

    console.log('Deleting tab:', tabId);

    // Find the tab being deleted
    const tabToDelete = tabs.find(tab => tab.id === tabId);

    // Delete founders that ONLY exist in this tab (not in any other tab)
    if (tabToDelete && tabToDelete.founderIds && tabToDelete.founderIds.length > 0) {
      // Find all other tabs (not the one being deleted)
      const otherTabs = tabs.filter(tab => tab.id !== tabId);

      // Get all founder IDs that exist in other tabs
      const founderIdsInOtherTabs = new Set<string>();
      otherTabs.forEach(tab => {
        if (tab.founderIds) {
          tab.founderIds.forEach(id => founderIdsInOtherTabs.add(id));
        }
      });

      // Only delete founders that are NOT in any other tab
      const foundersToDelete = tabToDelete.founderIds.filter(
        founderId => !founderIdsInOtherTabs.has(founderId)
      );

      if (foundersToDelete.length > 0) {
        console.log(`Deleting ${foundersToDelete.length} founders from database (${tabToDelete.founderIds.length - foundersToDelete.length} exist in other tabs)...`);
        const { apiService } = await import('../services/api');

        // Delete each founder from the database with timeout
        const deletePromises = foundersToDelete.map(founderId =>
          Promise.race([
            apiService.deleteFounder(founderId),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Delete timeout')), 10000)
            )
          ]).catch(err => {
            console.error(`Failed to delete founder ${founderId}:`, err);
            // Continue even if individual deletions fail
          })
        );

        try {
          await Promise.all(deletePromises);
          console.log(`${foundersToDelete.length} founders deleted from database`);
        } catch (error) {
          console.error('Error during bulk founder deletion:', error);
          // Continue with tab deletion even if some founders failed to delete
        }
      } else {
        console.log('No founders to delete - all exist in other tabs');
      }
    }

    // Now remove the tab from localStorage
    const wasActiveTab = tabId === activeTabId;
    let remainingTabs: TabConfig[] = [];
    
    setTabs(prev => {
      remainingTabs = prev.filter(tab => tab.id !== tabId);
      console.log('Filtered tabs:', remainingTabs.length, 'Tab IDs:', remainingTabs.map(t => t.id));
      return remainingTabs;
    });
    
    // If we deleted the active tab, switch to the first remaining tab
    if (wasActiveTab && remainingTabs.length > 0) {
      console.log('Switching to first remaining tab:', remainingTabs[0].id);
      setActiveTabId(remainingTabs[0].id);
    }
  }, [tabs, activeTabId]);

  const switchTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const hideFounderInActiveTab = useCallback((founderId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab;
      // If tab uses founderIds, remove from the list
      if (tab.founderIds !== undefined) {
        return { ...tab, founderIds: tab.founderIds.filter(id => id !== founderId) };
      }
      // Otherwise, use hiddenFounderIds (legacy)
      return { ...tab, hiddenFounderIds: [...(tab.hiddenFounderIds || []), founderId] };
    }));
  }, [activeTabId]);

  const addFounderToActiveTab = useCallback((founderId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab; // Only modify the active tab
      // Initialize founderIds if it doesn't exist (for legacy tabs)
      const currentFounderIds = tab.founderIds || [];
      if (!currentFounderIds.includes(founderId)) {
        return { ...tab, founderIds: [...currentFounderIds, founderId] };
      }
      return tab;
    }));
  }, [activeTabId]);

  const addFounderToTabByName = useCallback((founderId: string, tabName: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.name !== tabName) return tab; // Only modify the matching tab

      // Initialize founderIds if it doesn't exist (for legacy tabs)
      const currentFounderIds = tab.founderIds || [];

      if (!currentFounderIds.includes(founderId)) {
        console.log(`🔄 Adding founder ${founderId} to tab "${tabName}"`);
        return { ...tab, founderIds: [...currentFounderIds, founderId] };
      }

      return tab;
    }));
  }, [tabs]);

  const addFounderToTabById = useCallback((founderId: string, tabId: string) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab; // Only modify the matching tab

      // Initialize founderIds if it doesn't exist (for legacy tabs)
      const currentFounderIds = tab.founderIds || [];

      if (!currentFounderIds.includes(founderId)) {
        console.log(`🔄 Adding founder ${founderId} to tab "${tab.name}" (id: ${tabId})`);
        return { ...tab, founderIds: [...currentFounderIds, founderId] };
      }

      return tab;
    }));
  }, []);

  return {
    tabs,
    activeTab,
    activeTabId,
    addTab,
    updateActiveTab,
    renameTab,
    deleteTab,
    switchTab,
    hideFounderInActiveTab,
    addFounderToActiveTab,
    addFounderToTabByName,
    addFounderToTabById
  };
};

